import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getAthleteAssignments, logSession, getSession, getSessionHistory,
  updateSession, getLastSessionForDay, createSelfPlan, deleteSelfPlan,
} from '../../services/endpoints/trainingService';
import type {
  AthleteAssignmentDetail, TrainingDay, TrainingExercise,
  SessionSummary, SessionDetail, LogSessionForm, LogSetForm, UpdateSessionForm,
} from '../../types/training';
import { useCoachView } from '../../contexts/CoachViewContext';
import CoachCommentThread from '../../components/coach/CoachCommentThread';
import messageService from '../../services/endpoints/messageService';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns colour-coded "done X days ago" badge text and style for a day card. */
function getDayCompletionBadge(
  recentSessions: SessionSummary[],
  trainingDayId: string,
): { label: string; cls: string } | null {
  const match = recentSessions
    .filter(s => s.trainingDayId === trainingDayId)
    .sort((a, b) => new Date(b.completedOn).getTime() - new Date(a.completedOn).getTime())[0];

  if (!match) return null;

  const days = Math.floor(
    (Date.now() - new Date(match.completedOn).getTime()) / 86_400_000,
  );

  if (days === 0)  return { label: '✓ Done today',    cls: 'bg-green-900/50 text-green-300 border-green-700/40' };
  if (days === 1)  return { label: '✓ Yesterday',     cls: 'bg-green-900/30 text-green-400 border-green-700/30' };
  if (days <= 7)   return { label: `✓ ${days}d ago`,  cls: 'bg-blue-900/40  text-blue-300  border-blue-700/30'  };
  if (days <= 14)  return { label: `${days}d ago`,    cls: 'bg-gray-700/60  text-gray-400  border-gray-600/30'  };
  return              { label: `${days}d ago`,         cls: 'bg-gray-800/60  text-gray-600  border-gray-700/30'  };
}

/** Compact read-only summary of a single set from a previous session. */
function setRefLine(sets: SessionDetail['setLogs'], exId: string): string {
  const exSets = sets
    .filter(s => s.trainingExerciseId === exId)
    .sort((a, b) => a.setNumber - b.setNumber);
  if (!exSets.length) return '';
  return exSets
    .map(s => {
      const w = s.weightUsed != null ? `${s.weightUsed}${s.weightUnit ?? 'lbs'}` : 'BW';
      const r = s.repsCompleted != null ? `×${s.repsCompleted}` : '';
      return `${w}${r}`;
    })
    .join('  ');
}

// ── Shared score buttons ───────────────────────────────────────────────────────
const ScoreButtons: React.FC<{
  value?: number;
  onChange: (v: number) => void;
  label: string;
}> = ({ value, onChange, label }) => (
  <div>
    <label className="text-xs text-gray-400 block mb-1">{label}</label>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
            value === n
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

// ── Shared set editor grid ────────────────────────────────────────────────────
const SetGrid: React.FC<{
  sets: LogSetForm[];
  onUpdate: (exId: string, setNum: number, patch: Partial<LogSetForm>) => void;
}> = ({ sets, onUpdate }) => {
  const byExercise: Record<string, LogSetForm[]> = {};
  sets.forEach(s => {
    if (!byExercise[s.trainingExerciseId]) byExercise[s.trainingExerciseId] = [];
    byExercise[s.trainingExerciseId].push(s);
  });

  return (
    <>
      {Object.entries(byExercise).map(([exId, exSets]) => (
        <div key={exId} className="bg-gray-750 rounded-lg p-3 border border-gray-700">
          {(exSets[0] as any).exerciseName && (
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="text-white font-medium text-sm">{(exSets[0] as any).exerciseName}</span>
              {(exSets[0] as any).targetRIR !== undefined && (exSets[0] as any).targetRIR !== null && (
                <span className="bg-purple-900/50 text-purple-300 text-xs px-2 py-0.5 rounded-full">
                  Target RIR {(exSets[0] as any).targetRIR === 0 ? 'Failure' : (exSets[0] as any).targetRIR}
                </span>
              )}
              {(exSets[0] as any).prevRef && (
                <span className="text-xs text-gray-500 italic">Last: {(exSets[0] as any).prevRef}</span>
              )}
            </div>
          )}
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-1 text-xs text-gray-500 px-1">
              <div className="col-span-1">Set</div>
              <div className="col-span-3">Weight</div>
              <div className="col-span-1">Unit</div>
              <div className="col-span-2">Reps</div>
              <div className="col-span-2">Actual RIR</div>
              <div className="col-span-3">Notes</div>
            </div>
            {exSets.map(s => (
              <div key={s.setNumber} className="grid grid-cols-12 gap-1 items-center">
                <div className="col-span-1 text-gray-400 text-sm text-center">{s.setNumber}</div>
                <input
                  type="number" step="0.5" min="0"
                  value={s.weightUsed ?? ''}
                  onChange={e => onUpdate(exId, s.setNumber, { weightUsed: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="—"
                  className="col-span-3 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white text-center focus:border-blue-500 focus:outline-none"
                />
                <select
                  value={s.weightUnit ?? 'lbs'}
                  onChange={e => onUpdate(exId, s.setNumber, { weightUnit: e.target.value })}
                  className="col-span-1 bg-gray-700 border border-gray-600 rounded px-1 py-1 text-xs text-white"
                >
                  <option value="lbs">lbs</option>
                  <option value="kg">kg</option>
                </select>
                <input
                  type="number" min="0"
                  value={s.repsCompleted ?? ''}
                  onChange={e => onUpdate(exId, s.setNumber, { repsCompleted: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="—"
                  className="col-span-2 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white text-center focus:border-blue-500 focus:outline-none"
                />
                <select
                  value={s.actualRIR !== undefined ? String(s.actualRIR) : ''}
                  onChange={e => onUpdate(exId, s.setNumber, { actualRIR: e.target.value !== '' ? Number(e.target.value) : undefined })}
                  className="col-span-2 bg-gray-700 border border-gray-600 rounded px-1 py-1 text-sm text-white"
                >
                  <option value="">—</option>
                  <option value="0">0 (Failure)</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5+</option>
                </select>
                <input
                  type="text"
                  value={s.notes ?? ''}
                  onChange={e => onUpdate(exId, s.setNumber, { notes: e.target.value || undefined })}
                  placeholder="Notes"
                  className="col-span-3 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-white focus:border-blue-500 focus:outline-none"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

// ── Session Log Modal (new session) ───────────────────────────────────────────
interface SessionLogModalProps {
  assignment: AthleteAssignmentDetail;
  day: TrainingDay;
  onSave: () => void;
  onClose: () => void;
}

const SessionLogModal: React.FC<SessionLogModalProps> = ({ assignment, day, onSave, onClose }) => {
  // Use local date so users in negative-UTC offsets don't get "tomorrow"
  const _now = new Date();
  const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
  const [completedOn, setCompletedOn] = useState(today);
  const [energy, setEnergy] = useState<number | undefined>();
  const [pump, setPump] = useState<number | undefined>();
  const [rating, setRating] = useState<number | undefined>();
  const [duration, setDuration] = useState<number | undefined>();
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Coach note step (shown after successful save for coached assignments)
  const [savedSessionId, setSavedSessionId] = useState<string | null>(null);
  const [coachNote, setCoachNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const showNoteStep = !!savedSessionId && !assignment.isSelfCoached && !!assignment.relationshipId;

  // Previous session reference (for progressive overload context)
  const [prevSession, setPrevSession] = useState<SessionDetail | null>(null);
  const [prevLoading, setPrevLoading] = useState(true);
  const [showPrevRef, setShowPrevRef] = useState(true);

  // Build initial set rows (pre-populated with prev weights when available)
  const [sets, setSets] = useState<LogSetForm[]>(() =>
    day.exercises.flatMap(ex =>
      Array.from({ length: ex.sets }, (_, i) => ({
        trainingExerciseId: ex.trainingExerciseId,
        setNumber: i + 1,
        weightUnit: 'lbs',
        // carry exercise meta for SetGrid header
        ...({ exerciseName: ex.name, targetRIR: ex.targetRIR } as any),
      } as LogSetForm))
    )
  );

  // Load the most recent session for this day to show as reference
  useEffect(() => {
    setPrevLoading(true);
    getLastSessionForDay(day.trainingDayId, assignment.assignmentId)
      .then(s => {
        setPrevSession(s);
        if (s && s.setLogs.length > 0) {
          // Pre-populate weights from last session so athlete can adjust up
          setSets(prev => prev.map(row => {
            const prev_set = s.setLogs.find(
              sl => sl.trainingExerciseId === row.trainingExerciseId && sl.setNumber === row.setNumber
            );
            if (prev_set) {
              return {
                ...row,
                weightUsed: prev_set.weightUsed ?? undefined,
                weightUnit: prev_set.weightUnit ?? 'lbs',
                ...({ prevRef: setRefLine(s.setLogs, row.trainingExerciseId) } as any),
              };
            }
            return row;
          }));
        }
      })
      .catch(() => {})
      .finally(() => setPrevLoading(false));
  }, [day.trainingDayId, assignment.assignmentId]);

  const updateSet = (exId: string, setNum: number, patch: Partial<LogSetForm>) => {
    setSets(prev => prev.map(s =>
      s.trainingExerciseId === exId && s.setNumber === setNum ? { ...s, ...patch } : s
    ));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const form: LogSessionForm = {
        assignmentId: assignment.assignmentId,
        trainingDayId: day.trainingDayId,
        // Append noon (no Z) so the date-only string doesn't shift across UTC midnight
        completedOn: completedOn + 'T12:00:00',
        energyLevel: energy,
        pumpQuality: pump,
        overallRating: rating,
        durationMinutes: duration,
        notes: sessionNotes || undefined,
        sets: sets.filter(s => s.repsCompleted !== undefined || s.weightUsed !== undefined || s.actualRIR !== undefined),
      };
      const saved = await logSession(form);
      // For coached plans, pause and offer the athlete a chance to send a note before closing
      if (!assignment.isSelfCoached && assignment.relationshipId) {
        setSavedSessionId(saved.sessionLogId);
      } else {
        onSave();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSendNote = async () => {
    if (!coachNote.trim() || !savedSessionId || !assignment.relationshipId) return;
    setSendingNote(true);
    try {
      await messageService.sendMessage({
        relationshipId: assignment.relationshipId,
        body: coachNote.trim(),
        contextType: 'TrainingSession',
        contextId: savedSessionId,
      });
    } finally {
      setSendingNote(false);
      onSave();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-2xl my-4 space-y-4">
        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg">Log Session</h2>
            <p className="text-gray-400 text-sm">Day {day.dayNumber} — {day.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg">✕</button>
        </div>

        {/* Previous session reference banner */}
        {!prevLoading && prevSession && (
          <div className="mx-6">
            <button
              onClick={() => setShowPrevRef(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 bg-indigo-900/30 border border-indigo-700/40 rounded-lg text-xs text-indigo-300 hover:bg-indigo-900/50 transition-colors"
            >
              <span className="font-semibold">
                📊 Last session: {new Date(prevSession.completedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {prevSession.overallRating && (
                  <span className="ml-2 text-yellow-400">{'★'.repeat(prevSession.overallRating)}</span>
                )}
              </span>
              <span>{showPrevRef ? '▲ Hide' : '▼ Show'}</span>
            </button>
            {showPrevRef && (
              <div className="mt-1 bg-indigo-950/30 border border-indigo-700/30 rounded-lg px-3 py-2 space-y-1">
                {day.exercises.map(ex => {
                  const ref = setRefLine(prevSession.setLogs, ex.trainingExerciseId);
                  if (!ref) return null;
                  return (
                    <div key={ex.trainingExerciseId} className="flex items-start gap-2 text-xs">
                      <span className="text-gray-400 font-medium min-w-0 shrink-0">{ex.name}</span>
                      <span className="text-indigo-300 font-mono">{ref}</span>
                    </div>
                  );
                })}
                {prevSession.notes && (
                  <p className="text-gray-500 italic text-xs mt-1">Note: {prevSession.notes}</p>
                )}
              </div>
            )}
          </div>
        )}
        {!prevLoading && !prevSession && (
          <div className="mx-6 px-3 py-2 bg-gray-750 border border-gray-700 rounded-lg text-xs text-gray-500 italic">
            First time logging this day — no previous reference available.
          </div>
        )}

        <div className="px-6 space-y-4">
          {/* Date + Duration row */}
          <div className="flex gap-4 flex-wrap">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Date</label>
              <input
                type="date" value={completedOn}
                onChange={e => setCompletedOn(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Duration (min)</label>
              <input
                type="number" min="0" max="480" placeholder="e.g. 75"
                value={duration ?? ''}
                onChange={e => setDuration(e.target.value ? Number(e.target.value) : undefined)}
                className="w-28 bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Session ratings */}
          <div className="grid grid-cols-3 gap-4">
            <ScoreButtons value={energy}  onChange={setEnergy}  label="Energy Level" />
            <ScoreButtons value={pump}    onChange={setPump}    label="Pump Quality" />
            <ScoreButtons value={rating}  onChange={setRating}  label="Overall Rating" />
          </div>

          {/* Per-exercise set logging using SetGrid */}
          <div className="space-y-3">
            <SetGrid sets={sets} onUpdate={updateSet} />
          </div>

          {/* Session notes */}
          <div>
            <label className="text-xs text-gray-400 block mb-1">Session Notes (optional)</label>
            <textarea
              value={sessionNotes}
              onChange={e => setSessionNotes(e.target.value)}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-none focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Footer: Log Session button OR post-save coach note step */}
        {showNoteStep ? (
          <div className="px-6 pb-6 space-y-3">
            <div className="rounded-lg bg-green-900/20 border border-green-700/40 px-4 py-3">
              <p className="text-green-300 text-sm font-semibold">✅ Session logged!</p>
              <p className="text-gray-400 text-xs mt-0.5">
                Want to send a note to your coach about this session? (optional)
              </p>
            </div>
            <textarea
              rows={3}
              value={coachNote}
              onChange={e => setCoachNote(e.target.value)}
              placeholder="e.g. Felt strong today, pushed a PR on bench. Right shoulder a bit tight on the last set…"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-gold-500"
            />
            <div className="flex gap-3">
              <button
                onClick={handleSendNote}
                disabled={!coachNote.trim() || sendingNote}
                className="flex-1 py-2.5 bg-gold-500 hover:bg-gold-400 text-black rounded-lg font-semibold disabled:opacity-40 transition-colors"
              >
                {sendingNote ? 'Sending…' : '💬 Send Note & Done'}
              </button>
              <button
                onClick={onSave}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Skip
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 pb-5 flex gap-3">
            <button
              onClick={handleSubmit} disabled={saving}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {saving ? 'Logging…' : 'Log Session'}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Session Detail / Edit Modal ────────────────────────────────────────────────
interface SessionDetailModalProps {
  sessionId: string;
  viewAs?: string;
  isCoachView: boolean;
  /** When true the coach-notes thread starts pre-expanded. */
  openCoachNotes?: boolean;
  /** For athlete edit mode: the coach-athlete relationship ID so they can send a note on save. */
  relationshipId?: string;
  onClose: () => void;
  onUpdated: () => void;
}

const SessionDetailModal: React.FC<SessionDetailModalProps> = ({
  sessionId, viewAs, isCoachView, openCoachNotes = false, relationshipId, onClose, onUpdated,
}) => {
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [completedOn, setCompletedOn] = useState('');
  const [energy, setEnergy] = useState<number | undefined>();
  const [pump, setPump] = useState<number | undefined>();
  const [rating, setRating] = useState<number | undefined>();
  const [duration, setDuration] = useState<number | undefined>();
  const [notes, setNotes] = useState('');
  const [sets, setSets] = useState<LogSetForm[]>([]);
  // Coach note on save (athlete edit mode only)
  const [coachNote, setCoachNote] = useState('');

  useEffect(() => {
    setLoading(true);
    getSession(sessionId, viewAs)
      .then(s => {
        setSession(s);
        setCompletedOn(s.completedOn.split('T')[0]);
        setEnergy(s.energyLevel);
        setPump(s.pumpQuality);
        setRating(s.overallRating);
        setDuration(s.durationMinutes);
        setNotes(s.notes ?? '');
        setSets(s.setLogs.map(sl => ({
          trainingExerciseId: sl.trainingExerciseId,
          setNumber: sl.setNumber,
          weightUsed: sl.weightUsed,
          weightUnit: sl.weightUnit ?? 'lbs',
          repsCompleted: sl.repsCompleted,
          actualRIR: sl.actualRIR,
          notes: sl.notes,
          ...({ exerciseName: sl.exerciseName, targetRIR: sl.targetRIR } as any),
        } as LogSetForm)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sessionId, viewAs]);

  const updateSet = (exId: string, setNum: number, patch: Partial<LogSetForm>) => {
    setSets(prev => prev.map(s =>
      s.trainingExerciseId === exId && s.setNumber === setNum ? { ...s, ...patch } : s
    ));
  };

  const handleSave = async () => {
    if (!session) return;
    setSaving(true);
    try {
      const form: UpdateSessionForm = {
        // Append noon (no Z) so the date-only string doesn't shift across UTC midnight
        completedOn: completedOn + 'T12:00:00',
        energyLevel: energy,
        pumpQuality: pump,
        overallRating: rating,
        durationMinutes: duration,
        notes: notes || undefined,
        sets: sets.filter(s => s.repsCompleted !== undefined || s.weightUsed !== undefined || s.actualRIR !== undefined),
      };
      const updated = await updateSession(session.sessionLogId, form);
      // If the athlete added a coach note, send it to the comment thread
      if (!isCoachView && relationshipId && coachNote.trim()) {
        try {
          await messageService.sendMessage({
            relationshipId,
            body: coachNote.trim(),
            contextType: 'TrainingSession',
            contextId: session.sessionLogId,
          });
        } catch {
          // Note sending failure should not block the session save
        }
        setCoachNote('');
      }
      setSession(updated);
      setEditing(false);
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  const ratingStars = (n?: number) => n ? '★'.repeat(n) + '☆'.repeat(5 - n) : '—';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-2xl my-4">
        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg">
              {editing ? 'Edit Session' : 'Session Details'}
            </h2>
            {session && (
              <p className="text-gray-400 text-sm">
                {session.planName ? (
                  <><span className="text-gray-500">{session.planName}</span> · {session.dayName}</>
                ) : session.dayName}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isCoachView && session && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded"
              >
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-gray-500 hover:text-white text-lg">✕</button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : !session ? (
          <div className="p-8 text-center text-red-400 text-sm">Could not load session.</div>
        ) : editing ? (
          /* ── Edit mode ── */
          <div className="px-6 py-4 space-y-4">
            <div className="flex gap-4 flex-wrap">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Date</label>
                <input
                  type="date" value={completedOn}
                  onChange={e => setCompletedOn(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Duration (min)</label>
                <input
                  type="number" min="0" max="480" placeholder="e.g. 75"
                  value={duration ?? ''}
                  onChange={e => setDuration(e.target.value ? Number(e.target.value) : undefined)}
                  className="w-28 bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <ScoreButtons value={energy}  onChange={setEnergy}  label="Energy Level" />
              <ScoreButtons value={pump}    onChange={setPump}    label="Pump Quality" />
              <ScoreButtons value={rating}  onChange={setRating}  label="Overall Rating" />
            </div>

            <div className="space-y-3">
              <SetGrid sets={sets} onUpdate={updateSet} />
            </div>

            <div>
              <label className="text-xs text-gray-400 block mb-1">Session Notes</label>
              <textarea
                value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-none focus:border-blue-500 focus:outline-none"
              />
            </div>

            {!isCoachView && !!relationshipId && (
              <div>
                <label className="text-xs text-gray-400 block mb-1">
                  💬 Note to coach{' '}
                  <span className="text-gray-600 font-normal">(optional — sent on save)</span>
                </label>
                <textarea
                  rows={2}
                  value={coachNote}
                  onChange={e => setCoachNote(e.target.value)}
                  placeholder="Let your coach know about this edit…"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-gold-500"
                />
              </div>
            )}

            <div className="flex gap-3 pb-2">
              <button
                onClick={handleSave} disabled={saving}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* ── View mode ── */
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-750 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500 mb-0.5">Date</p>
                <p className="text-white text-sm font-medium">
                  {new Date(session.completedOn).toLocaleDateString()}
                </p>
              </div>
              {session.durationMinutes && (
                <div className="bg-gray-750 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-0.5">Duration</p>
                  <p className="text-white text-sm font-medium">{session.durationMinutes} min</p>
                </div>
              )}
              {session.energyLevel && (
                <div className="bg-gray-750 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-0.5">Energy</p>
                  <p className="text-yellow-400 text-sm">{ratingStars(session.energyLevel)}</p>
                </div>
              )}
              {session.pumpQuality && (
                <div className="bg-gray-750 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-0.5">Pump</p>
                  <p className="text-yellow-400 text-sm">{ratingStars(session.pumpQuality)}</p>
                </div>
              )}
              {session.overallRating && (
                <div className="bg-gray-750 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-0.5">Overall</p>
                  <p className="text-yellow-400 text-sm">{ratingStars(session.overallRating)}</p>
                </div>
              )}
            </div>

            {session.notes && (
              <div className="bg-gray-750 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Notes</p>
                <p className="text-gray-300 text-sm">{session.notes}</p>
              </div>
            )}

            {/* Per-exercise set data */}
            {(() => {
              const byEx: Record<string, typeof session.setLogs> = {};
              session.setLogs.forEach(sl => {
                const key = sl.exerciseName || sl.trainingExerciseId;
                if (!byEx[key]) byEx[key] = [];
                byEx[key].push(sl);
              });
              return Object.entries(byEx).map(([exName, setLogs]) => (
                <div key={exName} className="bg-gray-750 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-white font-medium text-sm">{exName}</span>
                    {setLogs[0]?.targetRIR !== undefined && setLogs[0]?.targetRIR !== null && (
                      <span className="bg-purple-900/50 text-purple-300 text-xs px-2 py-0.5 rounded-full">
                        Target RIR {setLogs[0].targetRIR === 0 ? 'Failure' : setLogs[0].targetRIR}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {setLogs.map(sl => (
                      <div key={sl.setLogId} className="flex items-center gap-3 text-xs text-gray-300 flex-wrap">
                        <span className="text-gray-500 w-8">Set {sl.setNumber}</span>
                        {sl.weightUsed !== undefined && sl.weightUsed !== null
                          ? <span>{sl.weightUsed} {sl.weightUnit}</span>
                          : <span className="text-gray-600">BW</span>}
                        {sl.repsCompleted !== undefined && <span>{sl.repsCompleted} reps</span>}
                        {sl.actualRIR !== undefined && sl.actualRIR !== null && (
                          <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                            sl.targetRIR !== undefined && sl.targetRIR !== null
                              ? sl.actualRIR === sl.targetRIR ? 'bg-green-900/50 text-green-300'
                                : sl.actualRIR > sl.targetRIR ? 'bg-yellow-900/50 text-yellow-300'
                                : 'bg-red-900/50 text-red-300'
                              : 'bg-gray-700 text-gray-400'
                          }`}>
                            RIR {sl.actualRIR === 0 ? 'Failure' : sl.actualRIR}
                          </span>
                        )}
                        {sl.notes && <span className="text-gray-500 italic">{sl.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}

            {/* Coach notes thread — visible to both coach (when viewing via viewAs) and athlete */}
            <CoachCommentThread
              contextType="TrainingSession"
              contextId={session.sessionLogId}
              label="session"
              defaultExpanded={openCoachNotes}
            />

            <div className="pb-2 text-center text-xs text-gray-600">
              Logged {new Date(session.loggedAt).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Create Self-Plan Modal ────────────────────────────────────────────────────
interface CreateSelfPlanModalProps {
  onCreated: (assignment: AthleteAssignmentDetail) => void;
  onClose: () => void;
}

const CreateSelfPlanModal: React.FC<CreateSelfPlanModalProps> = ({ onCreated, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Plan name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const assignment = await createSelfPlan({
        name: name.trim(),
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onCreated(assignment);
    } catch {
      setError('Failed to create plan. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md">
        <div className="px-6 pt-5 pb-3 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg">Create My Own Plan</h2>
            <p className="text-gray-400 text-xs mt-0.5">
              You can add days &amp; exercises after creating the plan.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg">✕</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Plan Name <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. 4-Day Push/Pull Split"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Short description of this plan"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Any notes about this plan"
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm resize-none focus:border-blue-500 focus:outline-none"
            />
          </div>
          {error && <p className="text-red-400 text-xs">{error}</p>}
        </div>

        <div className="px-6 pb-5 flex gap-3">
          <button
            onClick={handleSubmit} disabled={saving}
            className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {saving ? 'Creating…' : 'Create Plan'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Confirm Remove Plan Modal ──────────────────────────────────────────────────
interface ConfirmRemovePlanModalProps {
  planName: string;
  planId: string;
  onConfirmed: () => void;
  onClose: () => void;
}

const ConfirmRemovePlanModal: React.FC<ConfirmRemovePlanModalProps> = ({
  planName, planId, onConfirmed, onClose,
}) => {
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await deleteSelfPlan(planId);
      onConfirmed();
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-sm">
        <div className="px-6 py-5 space-y-3">
          <h2 className="text-white font-semibold text-lg">Remove Plan?</h2>
          <p className="text-gray-300 text-sm">
            <span className="font-medium text-white">"{planName}"</span> will be archived and removed from your active training.
          </p>
          <div className="bg-green-900/20 border border-green-700/40 rounded-lg px-3 py-2 text-xs text-green-300">
            ✓ Your session history for this plan will be preserved.
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button
            onClick={handleRemove} disabled={removing}
            className="flex-1 py-2.5 bg-red-700 hover:bg-red-600 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {removing ? 'Removing…' : 'Remove Plan'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Exercise Row (read-only) ───────────────────────────────────────────────────
const ExerciseRow: React.FC<{ exercise: TrainingExercise }> = ({ exercise: ex }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-gray-300 text-xs">{ex.name}</span>
    <span className="text-gray-500 text-xs">{ex.sets}×{ex.reps}</span>
    {ex.targetRIR !== undefined && ex.targetRIR !== null && (
      <span className="bg-purple-900/40 text-purple-300 text-xs px-1.5 py-0.5 rounded">
        RIR {ex.targetRIR === 0 ? 'Fail' : ex.targetRIR}
      </span>
    )}
    {ex.restSeconds && <span className="text-gray-600 text-xs">{ex.restSeconds}s rest</span>}
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const AthleteTraining: React.FC = () => {
  const { viewAs, isCoachView } = useCoachView();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [assignments, setAssignments] = useState<AthleteAssignmentDetail[]>([]);
  const [history, setHistory] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loggingDay, setLoggingDay] = useState<{ assignment: AthleteAssignmentDetail; day: TrainingDay } | null>(null);
  const [expandedAssignment, setExpandedAssignment] = useState<string | null>(null);
  const [viewingSessionId, setViewingSessionId] = useState<string | null>(null);
  const [viewingSessionRelId, setViewingSessionRelId] = useState<string | undefined>(undefined);
  const [openCoachNotes, setOpenCoachNotes] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmRemovePlan, setConfirmRemovePlan] = useState<{ planId: string; planName: string } | null>(null);

  // ── Deep-link from notification bell ─────────────────────────────────────
  // Watches searchParams so it fires even when the coach is already on
  // /training and React Router does a same-route navigation (no remount).
  // Coach-view params (viewAs, viewAsName, relationshipId) are preserved so
  // CoachViewContext stays active; only sessionId/openCoachNotes are stripped.
  // Safe against infinite loops: once sessionId is deleted the next run is a
  // no-op because the `if (sessionId)` guard is false.
  useEffect(() => {
    const sessionId = searchParams.get('sessionId');
    const openNotes = searchParams.get('openCoachNotes') === 'true';
    if (sessionId) {
      setViewingSessionId(sessionId);
      setOpenCoachNotes(openNotes);
      // Strip only the session-open params; leave coach-view params intact
      const next = new URLSearchParams(searchParams);
      next.delete('sessionId');
      next.delete('openCoachNotes');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([
        getAthleteAssignments(viewAs || undefined),
        getSessionHistory(viewAs || undefined, 1, 20),
      ]);
      setAssignments(a);
      setHistory(h);
      if (a.length > 0 && !expandedAssignment) setExpandedAssignment(a[0].assignmentId);
    } finally {
      setLoading(false);
    }
  }, [viewAs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-gray-400 text-sm p-4">Loading training…</div>;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">My Training</h1>
          <p className="text-gray-400 text-sm mt-1">
            {isCoachView ? "Viewing athlete's training plan" : 'Your assigned workout programs'}
          </p>
        </div>
        {/* Allow athletes (not coach view) to create their own plans */}
        {!isCoachView && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            + New Plan
          </button>
        )}
      </div>

      {assignments.length === 0 ? (
        /* ── Empty state with self-coaching CTA ── */
        <div className="text-center py-12 space-y-4">
          <p className="text-gray-400 text-lg">No active training plan</p>
          {!isCoachView && (
            <>
              <p className="text-gray-500 text-sm">
                Your coach can assign a plan, or you can build your own.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                🏋️ Create My Own Plan
              </button>
            </>
          )}
          {isCoachView && (
            <p className="text-gray-500 text-sm">This athlete has no active training assignments.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map(assignment => (
            <div key={assignment.assignmentId} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
              {/* Plan header */}
              <div className="flex items-center gap-2 px-5 py-4">
                <button
                  onClick={() => setExpandedAssignment(
                    expandedAssignment === assignment.assignmentId ? null : assignment.assignmentId
                  )}
                  className="flex-1 flex items-center justify-between hover:bg-gray-750 rounded-lg transition-colors text-left min-w-0"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-white font-semibold">{assignment.plan.name}</h2>
                      {assignment.isSelfCoached ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-700/40">
                          Self-coached
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-300 border border-blue-700/40">
                          Coached plan
                        </span>
                      )}
                    </div>
                    {assignment.plan.description && (
                      <p className="text-gray-400 text-sm mt-0.5">{assignment.plan.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-400 shrink-0 ml-4">
                    <span>{assignment.plan.days.length} days</span>
                    <span>{assignment.recentSessions.length} sessions</span>
                    <span className="text-gray-600">
                      {expandedAssignment === assignment.assignmentId ? '▲' : '▼'}
                    </span>
                  </div>
                </button>

                {/* Self-coached plan management buttons */}
                {assignment.isSelfCoached && !isCoachView && (
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <button
                      onClick={() => navigate(`/training/plans?planId=${assignment.plan.trainingPlanId}`)}
                      className="text-xs px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded transition-colors"
                      title="Edit plan structure (days & exercises)"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => setConfirmRemovePlan({
                        planId: assignment.plan.trainingPlanId,
                        planName: assignment.plan.name,
                      })}
                      className="text-xs px-3 py-1.5 bg-gray-700 hover:bg-red-900/60 text-gray-400 hover:text-red-300 rounded transition-colors"
                      title="Archive this plan (history is preserved)"
                    >
                      🗑
                    </button>
                  </div>
                )}
              </div>

              {expandedAssignment === assignment.assignmentId && (
                <div className="border-t border-gray-700">
                  {/* Days grid */}
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {assignment.plan.days.map(day => {
                      const badge = getDayCompletionBadge(assignment.recentSessions, day.trainingDayId);
                      return (
                        <div key={day.trainingDayId} className="bg-gray-750 rounded-lg p-3 border border-gray-700">
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-blue-400 font-semibold uppercase">
                                  Day {day.dayNumber}
                                </span>
                                <span className="text-white text-sm font-medium truncate">{day.name}</span>
                              </div>
                              {/* Completion badge */}
                              {badge && (
                                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full border ${badge.cls}`}>
                                  {badge.label}
                                </span>
                              )}
                            </div>
                            {!isCoachView && (
                              <button
                                onClick={() => setLoggingDay({ assignment, day })}
                                className="text-xs px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors shrink-0"
                              >
                                Log
                              </button>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            {day.exercises.map(ex => (
                              <ExerciseRow key={ex.trainingExerciseId} exercise={ex} />
                            ))}
                            {day.exercises.length === 0 && (
                              <p className="text-gray-600 text-xs italic">No exercises</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Recent sessions — clickable */}
                  {assignment.recentSessions.length > 0 && (
                    <div className="px-4 pb-4">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        Recent Sessions
                      </h3>
                      <div className="space-y-1">
                        {assignment.recentSessions.map(s => (
                          <button
                            key={s.sessionLogId}
                            onClick={() => {
                              setViewingSessionId(s.sessionLogId);
                              setViewingSessionRelId(assignment.relationshipId);
                            }}
                            className="w-full flex items-center gap-3 text-sm bg-gray-750 hover:bg-gray-700 rounded px-3 py-2 text-left transition-colors group"
                          >
                            <span className="text-gray-300">{new Date(s.completedOn).toLocaleDateString()}</span>
                            <span className="text-gray-400">{s.dayName}</span>
                            <span className="text-gray-500 text-xs">{s.setCount} sets</span>
                            {s.durationMinutes && (
                              <span className="text-gray-500 text-xs">{s.durationMinutes} min</span>
                            )}
                            {s.overallRating && (
                              <span className="text-xs text-yellow-400 ml-auto">
                                {'★'.repeat(s.overallRating)}
                              </span>
                            )}
                            <span className="text-gray-600 group-hover:text-blue-400 text-xs ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                              View →
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Plan History — show when coach has edited the plan */}
                  {assignment.changeLogs && assignment.changeLogs.length > 0 && (
                    <div className="px-4 pb-4">
                      <h3 className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-2">
                        📋 Plan History
                      </h3>
                      <div className="space-y-2">
                        {assignment.changeLogs.map(cl => (
                          <div
                            key={cl.changeLogId}
                            className="bg-purple-900/10 border border-purple-800/30 rounded-lg p-3"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-purple-300 font-medium">
                                {new Date(cl.changedAt).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', year: 'numeric',
                                })}
                              </span>
                              <span className="text-xs text-gray-500">by {cl.coachName}</span>
                            </div>
                            {cl.coachNotes && (
                              <p className="text-gray-300 text-sm mt-1">{cl.coachNotes}</p>
                            )}
                            {cl.aiSummary && cl.aiStatus === 'Ready' && (
                              <p className="text-gray-400 text-xs mt-1.5 italic border-t border-gray-700 pt-1.5">
                                🤖 {cl.aiSummary}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Session history table */}
      {history.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Session History</h2>
          <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-750 text-gray-400 text-xs uppercase border-b border-gray-700">
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Plan</th>
                  <th className="px-4 py-2 text-left">Day</th>
                  <th className="px-4 py-2 text-left">Sets</th>
                  <th className="px-4 py-2 text-left">Duration</th>
                  <th className="px-4 py-2 text-left">Energy</th>
                  <th className="px-4 py-2 text-left">Pump</th>
                  <th className="px-4 py-2 text-left">Rating</th>
                  <th className="px-4 py-2 text-left">Coach</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {history.map(s => (
                  <tr
                    key={s.sessionLogId}
                    onClick={() => {
                      setViewingSessionId(s.sessionLogId);
                      const asgmt = assignments.find(a => a.recentSessions.some(rs => rs.sessionLogId === s.sessionLogId));
                      setViewingSessionRelId(asgmt?.relationshipId);
                    }}
                    className="text-gray-300 hover:bg-gray-750 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2 whitespace-nowrap">{new Date(s.completedOn).toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs max-w-[160px] truncate">{s.planName ?? '—'}</td>
                    <td className="px-4 py-2">{s.dayName}</td>
                    <td className="px-4 py-2">{s.setCount}</td>
                    <td className="px-4 py-2">{s.durationMinutes ? `${s.durationMinutes} min` : '—'}</td>
                    <td className="px-4 py-2">{s.energyLevel ?? '—'}</td>
                    <td className="px-4 py-2">{s.pumpQuality ?? '—'}</td>
                    <td className="px-4 py-2">{s.overallRating ? '★'.repeat(s.overallRating) : '—'}</td>
                    <td className="px-4 py-2">
                      {s.hasCoachNote && (
                        <button
                          title="Coach note added — click to view"
                          onClick={e => {
                            e.stopPropagation();
                            setViewingSessionId(s.sessionLogId);
                            setOpenCoachNotes(true);
                            const asgmt = assignments.find(a => a.recentSessions.some(rs => rs.sessionLogId === s.sessionLogId));
                            setViewingSessionRelId(asgmt?.relationshipId);
                          }}
                          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30 transition-colors"
                        >
                          💬 coach note
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Log new session modal */}
      {loggingDay && (
        <SessionLogModal
          assignment={loggingDay.assignment}
          day={loggingDay.day}
          onSave={() => { setLoggingDay(null); load(); }}
          onClose={() => setLoggingDay(null)}
        />
      )}

      {/* View / edit existing session modal */}
      {viewingSessionId && (
        <SessionDetailModal
          sessionId={viewingSessionId}
          viewAs={viewAs || undefined}
          isCoachView={isCoachView}
          openCoachNotes={openCoachNotes}
          relationshipId={viewingSessionRelId}
          onClose={() => { setViewingSessionId(null); setOpenCoachNotes(false); setViewingSessionRelId(undefined); load(); }}
          onUpdated={() => load()}
        />
      )}

      {/* Create self-plan modal */}
      {showCreateModal && (
        <CreateSelfPlanModal
          onCreated={newAssignment => {
            setShowCreateModal(false);
            setAssignments(prev => [newAssignment, ...prev]);
            setExpandedAssignment(newAssignment.assignmentId);
          }}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Confirm remove self-plan modal */}
      {confirmRemovePlan && (
        <ConfirmRemovePlanModal
          planId={confirmRemovePlan.planId}
          planName={confirmRemovePlan.planName}
          onConfirmed={() => {
            setConfirmRemovePlan(null);
            load();
          }}
          onClose={() => setConfirmRemovePlan(null)}
        />
      )}
    </div>
  );
};

export default AthleteTraining;
